import React from 'react';
import { Box, Typography, Avatar, AvatarGroup, useTheme } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { formatTOI } from '../utils/shiftUtils';
import { PlayerShiftSummary } from '../types/PlayerShiftTypes';

interface PlayerInfoHeaderProps {
  summary: PlayerShiftSummary;
  headshot?: string;
  teamLogoPath: string;
  teamColor: string;
}

export const PlayerInfoHeader: React.FC<PlayerInfoHeaderProps> = ({
  summary,
  headshot,
  teamLogoPath,
  teamColor,
}) => {
  const theme = useTheme();
  const borderColor = theme.palette.cx.border.default;

  return (
    <Box
      sx={{
        px: 1.5,
        pt: 1,
        pb: 1.5,
        background: theme.palette.cx.bg.surface,
      }}
    >
      {/* Player card */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderRadius: 1,
          p: 1.5,
          background: theme.palette.cx.bg.elevated,
          border: `1px solid ${borderColor}`,
        }}
      >
        {/* Avatar group: headshot + team logo */}
        <Box sx={{ position: 'relative', width: 48, height: 40 }}>
          <AvatarGroup
            spacing={16}
            sx={{
              '& .MuiAvatar-root': { border: 'none' },
              '& .MuiAvatar-root:first-of-type': {
                zIndex: 1,
                position: 'absolute',
                left: 14,
                top: 0,
                width: 40,
                height: 40,
                backgroundColor: teamColor,
              },
              '& .MuiAvatar-root:last-of-type': {
                zIndex: 2,
                position: 'absolute',
                right: 0,
                left: 14,
                bottom: 0,
                width: 40,
                height: 40,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
              },
            }}
          >
            <Avatar
              alt={summary.playerName}
              src={headshot || undefined}
              sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
            >
              {!headshot && <PersonIcon sx={{ fontSize: 22, color: 'rgba(255,255,255,0.5)' }} />}
            </Avatar>
            <Avatar alt="Team Logo" src={teamLogoPath} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}>
              {summary.playerName.charAt(0).toUpperCase()}
            </Avatar>
          </AvatarGroup>
        </Box>

        {/* Player info */}
        <Box sx={{ flex: 1, ml: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 400, color: 'white', lineHeight: 1.2 }}>
            {summary.playerName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {[
              `#${summary.jerseyNumber}`,
              summary.position,
              `${formatTOI(summary.totalTOI)} TOI`,
              `${summary.totalShifts} Shifts`,
              `${summary.totalEvents} Events`,
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '8px', pt: 0.5 }}>
                {i > 0 && <Box sx={{ width: '1px', height: '18px', bgcolor: '#4A4A55', flexShrink: 0 }} />}
                <Typography sx={{
                  color: theme.palette.cx.text.tertiary,
                  fontFamily: 'Sohne',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  letterSpacing: 0,
                }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
